import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import FieldFactory from './FieldFactory';
import SignatureCanvas from 'react-signature-canvas';
import { formatFecha } from '../../../resources/Helper';
import { generarPdfConTextoNativo } from '../../../utils/pdfHelpers';
import { evaluateFormula } from '../../../utils/formulaEvaluator';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Loading from '../general/Loading';

/**
 * HistoriaClinicaJsonPage - Página completa para llenar historia clínica usando plantillas JSON
 * 
 * Props: Ninguno (lee pacienteId desde URL query params)
 */
const HistoriaClinicaJsonPage = () => {
    const history = useHistory();
    const queryParams = useLocation().search;
    const paciente_id = new URLSearchParams(queryParams).get('id');
    
    const [loggedIn, setLoggedIn] = useState(true);
    const [loadingPaciente, setLoadingPaciente] = useState(true);
    const [errorPaciente, setErrorPaciente] = useState(null);
    const [paciente, setPaciente] = useState(null);
    const [pacienteId, setPacienteId] = useState(paciente_id);
    const [plantillas, setPlantillas] = useState([]);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
    const [plantillaId, setPlantillaId] = useState('');
    const [valores, setValores] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingPlantillas, setLoadingPlantillas] = useState(false);
    
    // 🆕 Estados adicionales siguiendo patrón de ModalEvolucion
    const [fechaHora, setFechaHora] = useState('');
    const [nombreProfesional, setNombreProfesional] = useState('');
    const [registroMedico, setRegistroMedico] = useState('');
    const [codigoHabilitacion, setCodigoHabilitacion] = useState('');
    const [firmaimg, setFirmaimg] = useState('');
    
    // 🆕 Ref para firma del paciente
    const canvasRef = useRef(null);
    
    // 🆕 Estados para historial y modales
    const [historial, setHistorial] = useState([]);
    const [showModalVistaPrevia, setShowModalVistaPrevia] = useState(false);
    const [historiaSeleccionada, setHistoriaSeleccionada] = useState(null);
    const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
    const [showModalConsolidado, setShowModalConsolidado] = useState(false);
    const [consolidado, setConsolidado] = useState('');
    const [encabezadoPersonalizado, setEncabezadoPersonalizado] = useState(null);
    
    // 🆕 Cargar datos del paciente desde URL
    useEffect(() => {
        if (paciente_id) {
            setLoadingPaciente(true);
            setErrorPaciente(null);
            
            // Usar el mismo endpoint que ModalEvolucion.js con query params
            const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
            const configPaciente = {
                headers: {
                    Authorization: 'Bearer ' + userdata.access_token
                },
                params: {
                    id: paciente_id
                }
            };
            
            console.log('🔍 Cargando datos del paciente con ID:', paciente_id);
            
            axios.get('/paciente', configPaciente)
                .then(response => {
                    if (response.status === 200) {
                        console.log('✅ Datos del paciente recibidos del backend:', response.data);
                        
                        // El endpoint /paciente devuelve un array con un objeto
                        const datosPaciente = Array.isArray(response.data) ? response.data[0] : response.data;
                        
                        console.log('📋 Datos del paciente procesados:', {
                            primerNombre: datosPaciente.primer_nombre,
                            segundoNombre: datosPaciente.segundo_nombre,
                            primerApellido: datosPaciente.primer_apellido,
                            segundoApellido: datosPaciente.segundo_apellido,
                            tipoDocumento: datosPaciente.tipo_documento,
                            numeroDocumento: datosPaciente.numero_documento
                        });
                        
                        // Normalizar nombres de campos (snake_case a camelCase)
                        const pacienteNormalizado = {
                            primerNombre: datosPaciente.primer_nombre,
                            segundoNombre: datosPaciente.segundo_nombre,
                            primerApellido: datosPaciente.primer_apellido,
                            segundoApellido: datosPaciente.segundo_apellido,
                            tipoDocumento: datosPaciente.tipo_documento,
                            numeroDocumento: datosPaciente.numero_documento,
                            ...datosPaciente
                        };
                        
                        setPaciente(pacienteNormalizado);
                        console.log('✅ Estado paciente actualizado:', pacienteNormalizado);
                    }
                    setLoadingPaciente(false);
                })
                .catch(error => {
                    console.error('❌ Error cargando datos del paciente:', error);
                    console.error('❌ Detalles del error:', error.response?.data);
                    setErrorPaciente('No se pudieron cargar los datos del paciente');
                    setLoadingPaciente(false);
                });
        } else {
            console.error('❌ No se proporcionó ID de paciente en la URL');
            setErrorPaciente('No se proporcionó ID de paciente en la URL');
            setLoadingPaciente(false);
        }
    }, [paciente_id]);
    
    // 🆕 HELPER: Obtener datos del paciente (nombre completo y documento)
    const obtenerDatosPaciente = () => {
        console.log('🔍 obtenerDatosPaciente - paciente:', paciente);
        
        if (!paciente) {
            console.warn('⚠️ No hay datos del paciente disponibles');
            return {
                nombreCompleto: 'Paciente no identificado',
                documentoCompleto: 'Documento no disponible'
            };
        }
        
        // Construir nombre completo
        const primerNombre = paciente.primerNombre || paciente.primer_nombre || '';
        const segundoNombre = paciente.segundoNombre || paciente.segundo_nombre || '';
        const primerApellido = paciente.primerApellido || paciente.primer_apellido || '';
        const segundoApellido = paciente.segundoApellido || paciente.segundo_apellido || '';
        const tipoDocumento = paciente.tipoDocumento || paciente.tipo_documento || '';
        const numeroDocumento = paciente.numeroDocumento || paciente.numero_documento || '';
        
        const nombreCompleto = `${primerNombre} ${segundoNombre} ${primerApellido} ${segundoApellido}`.trim() || 'Paciente no identificado';
        
        // Construir documento completo solo si hay datos válidos
        let documentoCompleto = 'Documento no disponible';
        if (tipoDocumento && numeroDocumento) {
            documentoCompleto = `${tipoDocumento} - ${numeroDocumento}`;
        } else if (numeroDocumento) {
            documentoCompleto = numeroDocumento;
        }
        
        console.log('✅ obtenerDatosPaciente - resultado:', { nombreCompleto, documentoCompleto });
        
        return {
            nombreCompleto,
            documentoCompleto,
            ...paciente
        };
    };
    
    // Cargar plantillas, datos profesional e historial al montar
    useEffect(() => {
        if (paciente_id) {
            cargarPlantillas();
            cargarDatosProfesional();
            cargarHistorial();
        }
    }, [paciente_id]);
    
    // 🆕 Cargar historial de historias clínicas
    const cargarHistorial = () => {
        if (!pacienteId) {
            console.log('⚠️ No hay pacienteId, no se carga historial');
            return;
        }
        
        console.log('📥 Cargando historial para paciente:', pacienteId);
        
        axios.get('/historia-clinica-json', {
            params: { paciente_id: pacienteId }
        })
            .then(response => {
                console.log('✅ Historial cargado exitosamente');
                console.log('📋 Cantidad de registros:', response.data.length);
                console.log('📄 Datos completos:', response.data);
                
                // Log detallado del primer registro si existe
                if (response.data.length > 0) {
                    const primerRegistro = response.data[0];
                    console.log('🔍 Primer registro detallado:');
                    console.log('  - ID:', primerRegistro.id);
                    console.log('  - Fecha:', primerRegistro.fecha);
                    console.log('  - Plantilla:', primerRegistro.plantilla_nombre);
                    console.log('  - Datos (tipo):', typeof primerRegistro.datos);
                    console.log('  - Datos (valor):', primerRegistro.datos);
                    console.log('  - Estructura (tipo):', typeof primerRegistro.plantilla_estructura);
                    console.log('  - Estructura (valor):', primerRegistro.plantilla_estructura);
                }
                
                setHistorial(response.data);
            })
            .catch(error => {
                console.error('❌ Error cargando historial:', error);
                console.error('📋 Detalles del error:', {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    message: error.message
                });
                setHistorial([]);
            });
    };
    
    // 🆕 Cargar datos del profesional (siguiendo patrón de ModalEvolucion)
    const cargarDatosProfesional = () => {
        axios.get('/perfil')
            .then(response => {
                if (response.status === 200) {
                    setNombreProfesional(response.data.name + ' ' + response.data.lastname);
                    setRegistroMedico(response.data.registro_medico);
                    setCodigoHabilitacion(response.data.codigo_habilitacion);
                    setFirmaimg(response.data.firma_blob);
                }
            })
            .catch(error => {
                console.error('Error cargando datos del profesional:', error);
            });
        
        // 🆕 Cargar encabezado personalizado
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        axios.get('/encabezado-personalizado?uid=' + userdata.user_id)
            .then(response => {
                if (response.status === 200 && response.data && response.data.estado === 1) {
                    setEncabezadoPersonalizado(response.data);
                }
            })
            .catch(error => {
                console.log('No hay encabezado personalizado:', error);
                setEncabezadoPersonalizado(null);
            });
    };
    
    // 🆕 Establecer fecha y hora actual
    const establecerFechaHoraActual = () => {
        const ahora = new Date();
        const fechaFormateada = ahora.toISOString().slice(0, 16);
        setFechaHora(fechaFormateada);
    };
    
    const cargarPlantillas = () => {
        setLoadingPlantillas(true);
        
        axios.get('/mis-plantillas')
            .then(response => {
                setPlantillas(response.data);
                setLoadingPlantillas(false);
            })
            .catch(error => {
                console.error('Error cargando plantillas:', error);
                setLoadingPlantillas(false);
                alert('Error al cargar plantillas. Por favor intente nuevamente.');
            });
    };
    
    // Manejar cambio de plantilla
    const handlePlantillaChange = (e) => {
        const selectedId = e.target.value;
        setPlantillaId(selectedId);
        
        if (!selectedId) {
            setPlantillaSeleccionada(null);
            setValores({});
            return;
        }
        
        const plantilla = plantillas.find(p => p.id == selectedId);
        
        if (plantilla) {
            try {
                const estructura = typeof plantilla.estructura_json === 'string' 
                    ? JSON.parse(plantilla.estructura_json) 
                    : plantilla.estructura_json;
                
                setPlantillaSeleccionada(estructura);
                setValores({});
            } catch (error) {
                console.error('Error parseando estructura JSON:', error);
                alert('Error al cargar la estructura de la plantilla.');
            }
        }
    };
    
    // Manejar cambio de valor de campo
    const handleFieldChange = (campoId, valor) => {
        const nuevosValores = { ...valores, [campoId]: valor };
        
        // Recalcular todos los campos fórmula
        if (plantillaSeleccionada && plantillaSeleccionada.secciones) {
            plantillaSeleccionada.secciones.forEach(seccion => {
                if (seccion.campos) {
                    seccion.campos.forEach(campo => {
                        if (campo.tipo === 'formula') {
                            const resultado = evaluateFormula(campo.formula, nuevosValores);
                            if (resultado.value !== null) {
                                nuevosValores[campo.id] = Number(resultado.value.toFixed(campo.decimales ?? 2));
                            } else {
                                nuevosValores[campo.id] = '';
                            }
                        }
                    });
                }
            });
        }
        
        setValores(nuevosValores);
    };
    
    // Guardar historia clínica
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!plantillaId) {
            alert('Por favor seleccione una plantilla');
            return;
        }
        
        if (!fechaHora) {
            alert('Por favor establezca la fecha y hora');
            return;
        }
        
        setLoading(true);
        
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        
        const data = {
            paciente_id: pacienteId,
            plantilla_id: plantillaId,
            datos: JSON.stringify(valores),
            fecha: fechaHora, // 🆕 Usar fecha del input datetime-local
            profesional: nombreProfesional, // 🆕 Usar nombre del profesional cargado
            registro_medico: registroMedico || '',
            codigo_habilitacion: codigoHabilitacion || ''
        };
        
        // 🆕 Agregar firma del paciente si existe
        if (canvasRef.current && !canvasRef.current.isEmpty()) {
            data.firma_paciente = canvasRef.current.toDataURL('image/png');
        }
        
        // 🆕 Log detallado antes de enviar
        console.log('📤 Enviando datos de historia clínica JSON:', {
            paciente_id: data.paciente_id,
            plantilla_id: data.plantilla_id,
            fecha: data.fecha,
            profesional: data.profesional,
            registro_medico: data.registro_medico,
            codigo_habilitacion: data.codigo_habilitacion,
            tiene_firma_paciente: !!data.firma_paciente,
            cantidad_campos: Object.keys(valores).length
        });
        
        axios.post('/historia-clinica-json', data)
            .then(response => {
                setLoading(false);
                console.log('✅ Historia clínica guardada exitosamente:', response.data);
                
                // Limpiar formulario
                setPlantillaId('');
                setPlantillaSeleccionada(null);
                setValores({});
                setFechaHora('');
                if (canvasRef.current) {
                    canvasRef.current.clear();
                }
                
                // Recargar historial
                cargarHistorial();
                
                // 🆕 Mostrar modal de confirmación
                setTimeout(() => setShowModalConfirmacion(true), 500);
            })
            .catch(error => {
                setLoading(false);
                console.error('❌ Error guardando historia:', error);
                console.error('📋 Detalles del error:', {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    message: error.message
                });
                
                if (error.response && error.response.data && error.response.data.message) {
                    alert('Error: ' + error.response.data.message);
                } else {
                    alert('Error al guardar historia clínica. Por favor intente nuevamente.');
                }
            });
    };
    
    // 🆕 Función para nueva historia (limpiar formulario)
    const nuevaHistoria = () => {
        setPlantillaId('');
        setPlantillaSeleccionada(null);
        setValores({});
        setFechaHora('');
        setHistoriaSeleccionada(null);
        if (canvasRef.current) {
            canvasRef.current.clear();
        }
    };
    
    // 🆕 Ver historia clínica cargando datos en el formulario
    const verHistoria = (historia) => {
        console.log('📖 Cargando historia en formulario:', historia);
        
        // 1. Seleccionar la plantilla
        setPlantillaId(historia.plantilla_id);
        
        // 2. Parsear y cargar la estructura
        try {
            const estructura = typeof historia.plantilla_estructura === 'string' 
                ? JSON.parse(historia.plantilla_estructura) 
                : historia.plantilla_estructura;
            setPlantillaSeleccionada(estructura);
            
            // 3. Cargar los valores guardados
            const datos = typeof historia.datos === 'string' 
                ? JSON.parse(historia.datos) 
                : historia.datos;
            setValores(datos);
            
            // 4. Cargar fecha
            const fechaFormateada = historia.fecha.slice(0, 16);
            setFechaHora(fechaFormateada);
            
            // 5. Cargar firma del paciente si existe
            if (historia.firma_paciente && canvasRef.current) {
                console.log('🖊️ Cargando firma del paciente en canvas...');
                const img = new Image();
                img.onload = () => {
                    const canvas = canvasRef.current.getCanvas();
                    const ctx = canvas.getContext('2d');
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    console.log('✅ Firma del paciente cargada exitosamente');
                };
                img.onerror = () => {
                    console.error('❌ Error al cargar imagen de firma');
                };
                img.src = historia.firma_paciente;
            } else if (canvasRef.current) {
                // Si no hay firma, limpiar el canvas
                canvasRef.current.clear();
            }
            
            // 6. Marcar como seleccionada
            setHistoriaSeleccionada(historia);
            
            console.log('✅ Historia cargada en formulario exitosamente');
        } catch (error) {
            console.error('❌ Error al cargar historia en formulario:', error);
            alert('Error al cargar la historia. Por favor, intente nuevamente.');
        }
    };
    
    // 🆕 Generar encabezado HTML
    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) return '';
        
        const limpiarHTML = (html) => {
            if (!html) return '';
            return html
                .replace(/<p>/gi, '<div style="margin: 0; padding: 0; line-height: 1.1;">')
                .replace(/<\/p>/gi, '</div>')
                .replace(/<br\s*\/?>/gi, '<br style="line-height: 1.1;" />');
        };
        
        return `
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; background-color: #ffffff;">
                <tr>
                    <td style="width: 30%; padding: 2px; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.izquierda)}
                    </td>
                    <td style="width: 40%; padding: 2px; text-align: center; font-size: 11px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.centro)}
                    </td>
                    <td style="width: 30%; padding: 2px; text-align: right; font-size: 10px; line-height: 1.1; vertical-align: top; word-wrap: break-word;">
                        ${limpiarHTML(encabezadoPersonalizado.derecha)}
                    </td>
                </tr>
            </table>
        `;
    };
    
    // 🆕 Generar contenido HTML de la historia (ESTILO TABLAS CON BANDAS GRISES)
    const generarContenidoHistoriaHTML = (historia) => {
        console.log('📄 Generando HTML para historia:', historia);
        
        // 🆕 Usar helper para obtener datos del paciente
        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
        
        // 🆕 Obtener nombre de la plantilla
        const nombrePlantilla = historia.plantilla_nombre || 'Historia Clínica';
        
        // 1. BANNER: NOMBRE DE LA PLANTILLA (centrado y en negrita)
        let banner = `
            <div style="margin-bottom: 8px;">
                <div style="background-color: #e9ecef; padding: 6px 8px; font-weight: bold; font-size: 12px; text-align: center; border: 1px solid #dee2e6;">
                    ${nombrePlantilla.toUpperCase()}
                </div>
            </div>`;
        
        // 2. Información del Paciente (tabla con bandas grises - sin PLANTILLA)
        let tablaInfoPaciente = `
            <div style="margin-bottom: 8px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                    <tbody>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; width: 25%; border: 1px solid #dee2e6;">PACIENTE</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${nombreCompleto}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">DOCUMENTO</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${documentoCompleto}</td>
                        </tr>
                        <tr>
                            <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">FECHA Y HORA</td>
                            <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${new Date(historia.fecha).toLocaleString()}</td>
                        </tr>
                    </tbody>
                </table>
            </div>`;
        
        // 3. Datos de la historia (secciones con tablas)
        let tablaDatos = '';
        try {
            console.log('📊 Parseando datos de historia...');
            
            const datos = typeof historia.datos === 'string' ? JSON.parse(historia.datos) : historia.datos;
            const estructura = typeof historia.plantilla_estructura === 'string' 
                ? JSON.parse(historia.plantilla_estructura) 
                : historia.plantilla_estructura;
            
            if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                estructura.secciones.forEach(seccion => {
                    // Verificar si la sección tiene campos con valores
                    let seccionTieneContenido = false;
                    let filasSeccion = '';
                    
                    if (seccion.campos && Array.isArray(seccion.campos)) {
                        seccion.campos.forEach(campo => {
                            const valor = datos[campo.id];
                            if (valor !== undefined && valor !== null && valor !== '') {
                                seccionTieneContenido = true;
                                
                                // Formatear valor según tipo de campo
                                let valorFormateado = valor;
                                if (campo.tipo === 'checkbox') {
                                    valorFormateado = valor ? 'Sí' : 'No';
                                } else if (campo.tipo === 'textarea') {
                                    valorFormateado = valor.replace(/\n/g, '<br>');
                                }
                                
                                filasSeccion += `
                                    <tr>
                                        <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; width: 30%; border: 1px solid #dee2e6;">${campo.label}</td>
                                        <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6;">${valorFormateado}</td>
                                    </tr>`;
                            }
                        });
                    }
                    
                    // Solo mostrar sección si tiene contenido
                    if (seccionTieneContenido) {
                        tablaDatos += `
                            <div style="margin-bottom: 8px;">
                                <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                                    <tbody>
                                        <tr>
                                            <td colspan="2" style="background-color: #f8f9fa; font-weight: bold; padding: 6px 8px; border: 1px solid #dee2e6;">${seccion.titulo}</td>
                                        </tr>
                                        ${filasSeccion}
                                    </tbody>
                                </table>
                            </div>`;
                    }
                });
            }
            
            console.log('✅ Segmento de datos generado correctamente');
        } catch (error) {
            console.error('❌ Error parseando datos:', error);
            tablaDatos = `<div style="color: red; padding: 8px;">Error al cargar datos de la historia: ${error.message}</div>`;
        }
        
        // 4. Profesional Tratante y Firma (tabla con bandas grises)
        let tablaProfesional = '';
        if (historia.profesional) {
            tablaProfesional = `
                <div style="margin-top: 12px; page-break-inside: avoid;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                        <tbody>
                            <tr>
                                <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; width: 30%; border: 1px solid #dee2e6;">PROFESIONAL TRATANTE</td>
                                <td style="background-color: #ffffff; padding: 4px 8px; border: 1px solid #dee2e6; font-weight: bold; color: #325e68;">${historia.profesional}</td>
                            </tr>`;
            
            // Firma del profesional
            if (historia.firma) {
                tablaProfesional += `
                            <tr>
                                <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; border: 1px solid #dee2e6;">FIRMA DEL PROFESIONAL</td>
                                <td style="background-color: #ffffff; padding: 8px; border: 1px solid #dee2e6;">
                                    <img src="${historia.firma}" style="max-width: 180px; max-height: 90px; display: block;" />
                                    <div style="margin-top: 4px; font-size: 9px; color: #6c757d;">
                                        ${historia.profesional}<br/>
                                        <strong>RM:</strong> ${historia.registro_medico || 'N/A'} | <strong>Habilitación:</strong> ${historia.codigo_habilitacion || 'N/A'}
                                    </div>
                                </td>
                            </tr>`;
            }
            
            tablaProfesional += `
                        </tbody>
                    </table>
                </div>`;
        }
        
        // 5. Firma del Paciente (si existe)
        let tablaFirmaPaciente = '';
        if (historia.firma_paciente) {
            tablaFirmaPaciente = `
                <div style="margin-top: 8px; page-break-inside: avoid;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #dee2e6;">
                        <tbody>
                            <tr>
                                <td style="background-color: #f8f9fa; font-weight: bold; padding: 4px 8px; width: 30%; border: 1px solid #dee2e6;">FIRMA DEL PACIENTE</td>
                                <td style="background-color: #ffffff; padding: 8px; border: 1px solid #dee2e6;">
                                    <img src="${historia.firma_paciente}" style="max-width: 180px; max-height: 90px; display: block;" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>`;
        }
        
        return banner + tablaInfoPaciente + tablaDatos + tablaProfesional + tablaFirmaPaciente;
    };
    
    // 🆕 Descargar PDF usando jsPDF con texto nativo
    const descargarPdfHistoria = async () => {
        if (!historiaSeleccionada) return;
        
        try {
            // 🆕 Usar helper para obtener datos del paciente
            const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
            const docPaciente = paciente?.numeroDocumento || 'paciente';
            
            // 🆕 Obtener nombre de la plantilla
            const nombrePlantilla = historiaSeleccionada.plantilla_nombre || 'Historia Clínica';
            
            // Preparar configuración para generarPdfConTextoNativo
            const config = {
                filename: 'historia-clinica-' + docPaciente + '.pdf',
                paciente: {
                    nombreCompleto: nombreCompleto,
                    documentoCompleto: documentoCompleto
                },
                encabezadoPersonalizado: encabezadoPersonalizado ? {
                    izquierda: encabezadoPersonalizado.izquierda || '',
                    centro: encabezadoPersonalizado.centro || '',
                    derecha: encabezadoPersonalizado.derecha || ''
                } : null,
                secciones: [],
                firmas: []
            };
            
            // Banner de título con nombre de plantilla
            config.secciones.push({
                titulo: nombrePlantilla.toUpperCase(),
                tipo: 'banner',
                contenido: nombrePlantilla.toUpperCase()
            });
            
            // Tabla de fecha
            config.secciones.push({
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        { etiqueta: 'FECHA Y HORA', valor: new Date(historiaSeleccionada.fecha).toLocaleString() }
                    ]
                }
            });
            
            // Procesar datos de la historia (secciones con tablas)
            try {
                const datos = typeof historiaSeleccionada.datos === 'string' 
                    ? JSON.parse(historiaSeleccionada.datos) 
                    : historiaSeleccionada.datos;
                const estructura = typeof historiaSeleccionada.plantilla_estructura === 'string' 
                    ? JSON.parse(historiaSeleccionada.plantilla_estructura) 
                    : historiaSeleccionada.plantilla_estructura;
                
                if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                    estructura.secciones.forEach(seccion => {
                        // Verificar si la sección tiene campos con valores
                        const filasSeccion = [];
                        
                        if (seccion.campos && Array.isArray(seccion.campos)) {
                            seccion.campos.forEach(campo => {
                                const valor = datos[campo.id];
                                if (valor !== undefined && valor !== null && valor !== '') {
                                    // Formatear valor según tipo de campo
                                    let valorFormateado = valor;
                                    if (campo.tipo === 'checkbox') {
                                        valorFormateado = valor ? 'Sí' : 'No';
                                    } else if (campo.tipo === 'textarea') {
                                        valorFormateado = valor.replace(/\n/g, '<br>');
                                    }
                                    
                                    filasSeccion.push({
                                        etiqueta: campo.label,
                                        valor: valorFormateado
                                    });
                                }
                            });
                        }
                        
                        // Solo agregar sección si tiene contenido
                        if (filasSeccion.length > 0) {
                            // Agregar título de sección como banner
                            config.secciones.push({
                                titulo: seccion.titulo,
                                tipo: 'banner',
                                contenido: seccion.titulo
                            });
                            
                            // Agregar datos de la sección
                            config.secciones.push({
                                tipo: 'tabla-datos',
                                datos: {
                                    filas: filasSeccion
                                }
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parseando datos de historia:', error);
            }
            
            // Firmas lado a lado usando tipo 'firmas-grupo'
            const firmasGrupo = [];
            
            // Firma del profesional
            if (historiaSeleccionada.profesional && historiaSeleccionada.firma) {
                firmasGrupo.push({
                    imagenBase64: historiaSeleccionada.firma,
                    metadata: {
                        tipo: 'profesional',
                        nombre: historiaSeleccionada.profesional || '',
                        registroMedico: historiaSeleccionada.registro_medico || 'N/A',
                        codigoHabilitacion: historiaSeleccionada.codigo_habilitacion || 'N/A'
                    }
                });
            }
            
            // Firma del paciente
            if (historiaSeleccionada.firma_paciente) {
                firmasGrupo.push({
                    imagenBase64: historiaSeleccionada.firma_paciente,
                    metadata: {
                        tipo: 'paciente'
                    }
                });
            }
            
            // Agregar firmas como grupo si hay alguna
            if (firmasGrupo.length > 0) {
                config.secciones.push({
                    tipo: 'firmas-grupo',
                    firmas: firmasGrupo
                });
            }
            
            // Generar y descargar PDF
            const pdf = await generarPdfConTextoNativo(config);
            pdf.save(config.filename);
            
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };
    
    // 🆕 Imprimir PDF usando jsPDF con texto nativo
    const imprimirHistoria = async () => {
        if (!historiaSeleccionada) return;
        
        try {
            // 🆕 Usar helper para obtener datos del paciente
            const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
            const docPaciente = paciente?.numeroDocumento || 'paciente';
            
            // 🆕 Obtener nombre de la plantilla
            const nombrePlantilla = historiaSeleccionada.plantilla_nombre || 'Historia Clínica';
            
            // Preparar configuración para generarPdfConTextoNativo
            const config = {
                filename: 'historia-clinica-' + docPaciente + '.pdf',
                paciente: {
                    nombreCompleto: nombreCompleto,
                    documentoCompleto: documentoCompleto
                },
                encabezadoPersonalizado: encabezadoPersonalizado ? {
                    izquierda: encabezadoPersonalizado.izquierda || '',
                    centro: encabezadoPersonalizado.centro || '',
                    derecha: encabezadoPersonalizado.derecha || ''
                } : null,
                secciones: [],
                firmas: []
            };
            
            // Banner de título con nombre de plantilla
            config.secciones.push({
                titulo: nombrePlantilla.toUpperCase(),
                tipo: 'banner',
                contenido: nombrePlantilla.toUpperCase()
            });
            
            // Tabla de fecha
            config.secciones.push({
                tipo: 'tabla-datos',
                datos: {
                    filas: [
                        { etiqueta: 'FECHA Y HORA', valor: new Date(historiaSeleccionada.fecha).toLocaleString() }
                    ]
                }
            });
            
            // Procesar datos de la historia (secciones con tablas)
            try {
                const datos = typeof historiaSeleccionada.datos === 'string' 
                    ? JSON.parse(historiaSeleccionada.datos) 
                    : historiaSeleccionada.datos;
                const estructura = typeof historiaSeleccionada.plantilla_estructura === 'string' 
                    ? JSON.parse(historiaSeleccionada.plantilla_estructura) 
                    : historiaSeleccionada.plantilla_estructura;
                
                if (estructura && estructura.secciones && Array.isArray(estructura.secciones)) {
                    estructura.secciones.forEach(seccion => {
                        // Verificar si la sección tiene campos con valores
                        const filasSeccion = [];
                        
                        if (seccion.campos && Array.isArray(seccion.campos)) {
                            seccion.campos.forEach(campo => {
                                const valor = datos[campo.id];
                                if (valor !== undefined && valor !== null && valor !== '') {
                                    // Formatear valor según tipo de campo
                                    let valorFormateado = valor;
                                    if (campo.tipo === 'checkbox') {
                                        valorFormateado = valor ? 'Sí' : 'No';
                                    } else if (campo.tipo === 'textarea') {
                                        valorFormateado = valor.replace(/\n/g, '<br>');
                                    }
                                    
                                    filasSeccion.push({
                                        etiqueta: campo.label,
                                        valor: valorFormateado
                                    });
                                }
                            });
                        }
                        
                        // Solo agregar sección si tiene contenido
                        if (filasSeccion.length > 0) {
                            // Agregar título de sección como banner
                            config.secciones.push({
                                titulo: seccion.titulo,
                                tipo: 'banner',
                                contenido: seccion.titulo
                            });
                            
                            // Agregar datos de la sección
                            config.secciones.push({
                                tipo: 'tabla-datos',
                                datos: {
                                    filas: filasSeccion
                                }
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parseando datos de historia:', error);
            }
            
            // Firmas lado a lado usando tipo 'firmas-grupo'
            const firmasGrupo = [];
            
            // Firma del profesional
            if (historiaSeleccionada.profesional && historiaSeleccionada.firma) {
                firmasGrupo.push({
                    imagenBase64: historiaSeleccionada.firma,
                    metadata: {
                        tipo: 'profesional',
                        nombre: historiaSeleccionada.profesional || '',
                        registroMedico: historiaSeleccionada.registro_medico || 'N/A',
                        codigoHabilitacion: historiaSeleccionada.codigo_habilitacion || 'N/A'
                    }
                });
            }
            
            // Firma del paciente
            if (historiaSeleccionada.firma_paciente) {
                firmasGrupo.push({
                    imagenBase64: historiaSeleccionada.firma_paciente,
                    metadata: {
                        tipo: 'paciente'
                    }
                });
            }
            
            // Agregar firmas como grupo si hay alguna
            if (firmasGrupo.length > 0) {
                config.secciones.push({
                    tipo: 'firmas-grupo',
                    firmas: firmasGrupo
                });
            }
            
            // Generar PDF
            const pdf = await generarPdfConTextoNativo(config);
            
            // Abrir diálogo de impresión
            const blob = pdf.output('blob');
            const url = URL.createObjectURL(blob);
            const printWindow = window.open(url, '_blank');
            
            if (printWindow) {
                printWindow.onload = function() {
                    setTimeout(() => printWindow.print(), 500);
                };
            } else {
                // Fallback: iframe oculto
                console.log('Popup bloqueado, usando método alternativo');
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.src = url;
                document.body.appendChild(iframe);
                
                iframe.onload = function() {
                    setTimeout(() => {
                        try {
                            iframe.contentWindow.print();
                        } catch (e) {
                            alert('No se pudo abrir el diálogo de impresión. Por favor, descarga el PDF.');
                        }
                    }, 500);
                };
            }
        } catch (error) {
            console.error('Error generando PDF:', error);
            alert('Error al generar el PDF. Por favor, intenta nuevamente.');
        }
    };
    
    // 🆕 Funciones del modal de confirmación
    const handleCerrarModalConfirmacion = () => {
        setShowModalConfirmacion(false);
    };
    
    const handleImprimirDesdeConfirmacion = () => {
        setShowModalConfirmacion(false);
        if (historial && historial.length > 0) {
            const ultimaHistoria = historial[0];
            setHistoriaSeleccionada(ultimaHistoria);
            setTimeout(() => imprimirHistoria(), 300);
        }
    };
    
    const handleDescargarPdfDesdeConfirmacion = () => {
        setShowModalConfirmacion(false);
        if (historial && historial.length > 0) {
            const ultimaHistoria = historial[0];
            setHistoriaSeleccionada(ultimaHistoria);
            setTimeout(() => descargarPdfHistoria(), 300);
        }
    };
    
    // 🆕 Generar vista consolidada de todas las historias
    const generarVistaConsolidada = () => {
        console.log('📄 Generando vista consolidada de historias...');
        
        let consolidadoHTML = '';
        
        historial.forEach((historia, index) => {
            // Agregar separador entre historias (excepto la primera)
            if (index > 0) {
                consolidadoHTML += '<hr style="border-top: 2px solid #e0e0e0; margin: 25px 0;">';
            }
            
            // Generar contenido de cada historia
            consolidadoHTML += generarContenidoHistoriaHTML(historia);
        });
        
        setConsolidado(consolidadoHTML);
        setShowModalConsolidado(true);
    };
    
    // 🆕 Validar que hay pacienteId
    if (!paciente_id) {
        return (
            <>
                <Header />
                <Aside />
                <div className="content-wrapper">
                    <section className="content">
                        <div className="container-fluid">
                            <div className="alert alert-danger">
                                <i className="fas fa-exclamation-triangle"></i>
                                {' '}No se especificó un paciente. Por favor, vuelva e intente nuevamente.
                            </div>
                        </div>
                    </section>
                </div>
                <Footer />
            </>
        );
    }
    
    // 🆕 Mostrar loading mientras carga paciente
    if (loadingPaciente) {
        return <Loading />;
    }
    
    return (
        <>
            <style>{`
                /* Corregir comportamiento de hover en botones */
                .btn-primary, .btn-secondary {
                    transform: scale(1.05);
                    transition: transform 0.2s ease;
                }
                .btn-primary:hover, .btn-secondary:hover {
                    transform: scale(1);
                }
                .btn-primary:active, .btn-secondary:active {
                    transform: scale(0.98);
                }
            `}</style>
            <Header />
            <Aside />
            <div className="content-wrapper">
                <section className="content">
                    <div className="container-fluid">
                        {/* 🆕 Título y Botones de Navegación */}
                        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #343a40' }}>
                            <div className="card-body" style={{ padding: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <h4 className="mb-0" style={{ color: '#343a40' }}>
                                            <i className="fas fa-file-medical"></i> HISTORIA CLÍNICA DEL PACIENTE
                                        </h4>
                                        {/* Botones de navegación rápida */}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-dark"
                                            onClick={() => history.push(`/atender-paciente?id=${paciente_id}`)}
                                            title="Volver a Atención del Paciente"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-user-injured"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-info"
                                            onClick={() => history.push(`/estado-cuenta-paciente?id=${paciente_id}`)}
                                            title="Ver Cartera del Paciente"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-dollar-sign"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => history.goBack()}
                                            title="Volver"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* 🆕 Información del paciente debajo del título */}
                                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                                    {(() => {
                                        const { nombreCompleto, documentoCompleto } = obtenerDatosPaciente();
                                        return (
                                            <div style={{ display: 'flex', gap: '30px', fontSize: '14px' }}>
                                                <div>
                                                    <strong><i className="fas fa-user"></i> Paciente:</strong> {nombreCompleto}
                                                </div>
                                                <div>
                                                    <strong><i className="fas fa-id-card"></i> Documento:</strong> {documentoCompleto}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        
                        {/* 🆕 Botones de Acción */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <button 
                                    className="btn btn-success btn-sm mr-2"
                                    onClick={nuevaHistoria}
                                >
                                    <i className="fas fa-plus"></i> Nueva Historia
                                </button>
                                <button 
                                    className="btn btn-info btn-sm"
                                    onClick={() => historiaSeleccionada && setShowModalVistaPrevia(true)}
                                    disabled={!historiaSeleccionada}
                                >
                                    <i className="fas fa-eye"></i> Ver Resumen
                                </button>
                            </div>
                        </div>
                        
                        <div className="row">
                            {/* 🆕 Columna principal (9 columnas) */}
                            <div className="col-md-9">
                            {/* 1. Selector de Plantilla */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-file-medical"></i> Seleccionar Plantilla
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="form-group mb-0">
                                        <label><strong>Plantilla:</strong></label>
                                        <select 
                                            className="form-control" 
                                            value={plantillaId}
                                            onChange={handlePlantillaChange}
                                            disabled={loadingPlantillas}
                                        >
                                            <option value="">-- Seleccione una plantilla --</option>
                                            {plantillas.map(plantilla => (
                                                <option key={plantilla.id} value={plantilla.id}>
                                                    {plantilla.nombre} 
                                                    {plantilla.especialidad && ` (${plantilla.especialidad})`}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingPlantillas && (
                                            <small className="text-muted">
                                                <i className="fas fa-spinner fa-spin"></i> Cargando plantillas...
                                            </small>
                                        )}
                                        {!loadingPlantillas && plantillas.length === 0 && (
                                            <small className="text-warning">
                                                <i className="fas fa-exclamation-triangle"></i> No tiene plantillas asignadas. 
                                                Contacte al administrador.
                                            </small>
                                        )}
                                        {!loadingPlantillas && plantillas.length > 0 && !plantillaId && (
                                            <small className="text-info d-block mt-2">
                                                <i className="fas fa-info-circle"></i> Recuerde seleccionar la plantilla de historia clínica
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 2. Fecha y Hora */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">
                                        <i className="fas fa-clock"></i> Fecha y Hora
                                    </h5>
                                </div>
                                <div className="card-body">
                                    {/* Profesional Tratante */}
                                    <div className="form-group">
                                        <label><i className="fas fa-user-md"></i> Profesional Tratante</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={nombreProfesional}
                                            readOnly
                                            style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
                                        />
                                        <small className="text-muted">Usuario que realiza la historia clínica</small>
                                    </div>
                                    
                                    {/* Fecha y Hora */}
                                    <div className="form-group mb-0">
                                        <label><strong>Fecha y Hora:</strong></label>
                                        <div className="input-group">
                                            <input 
                                                type="datetime-local" 
                                                className="form-control" 
                                                value={fechaHora} 
                                                onChange={(e) => setFechaHora(e.target.value)}
                                            />
                                            <div className="input-group-append">
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary" 
                                                    onClick={establecerFechaHoraActual}
                                                >
                                                    <i className="fas fa-clock"></i> Fecha y Hora Actual
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            
                            {/* 3. Campos Dinámicos de la Plantilla */}
                            {plantillaSeleccionada && (
                                <form onSubmit={handleSubmit} id="formHistoriaJson">
                                    {plantillaSeleccionada.secciones && plantillaSeleccionada.secciones.map((seccion, seccionIndex) => (
                                        <div key={seccionIndex} className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h5 className="mb-0">
                                                    <i className="fas fa-folder-open"></i> {seccion.titulo}
                                                </h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    {seccion.campos && seccion.campos.map((campo, campoIndex) => (
                                                        <div 
                                                            key={campoIndex} 
                                                            className={campo.tipo === 'textarea' ? 'col-md-12' : 'col-md-6'}
                                                        >
                                                            <FieldFactory
                                                                campo={campo}
                                                                valor={valores[campo.id]}
                                                                onChange={handleFieldChange}
                                                                valores={valores}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* 4. Firma del Paciente */}
                                    <div className="card mb-3">
                                        <div className="card-header bg-light">
                                            <h5 className="mb-0">
                                                <i className="fas fa-signature"></i> Firma del Paciente
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="form-group mb-0">
                                                <SignatureCanvas 
                                                    ref={canvasRef} 
                                                    penColor='#242424' 
                                                    canvasProps={{ 
                                                        width: 300, 
                                                        height: 130, 
                                                        className: 'sigCanvas', 
                                                        style: { border: '1px solid #ddd' } 
                                                    }} 
                                                />
                                                <br />
                                                <button 
                                                    type="button"
                                                    className="btn btn-secondary btn-sm" 
                                                    onClick={(e) => { 
                                                        e.preventDefault(); 
                                                        if (canvasRef.current) {
                                                            canvasRef.current.clear(); 
                                                        }
                                                    }}
                                                >
                                                    <i className="fas fa-eraser"></i> Borrar Firma
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* 5. Información del Profesional */}
                                    <div className="card mb-3">
                                        <div className="card-header bg-light">
                                            <h5 className="mb-0">
                                                <i className="fas fa-user-md"></i> Profesional que Atiende
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <div className="form-group mb-0">
                                                        <label><strong>Profesional:</strong></label>
                                                        <div>{nombreProfesional}</div>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group mb-0">
                                                        <label><strong>Registro Médico:</strong></label>
                                                        <div>{registroMedico || 'N/A'}</div>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group mb-0">
                                                        <label><strong>Código de Habilitación:</strong></label>
                                                        <div>{codigoHabilitacion || 'N/A'}</div>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group mb-0">
                                                        <label><strong>Firma:</strong></label>
                                                        <br />
                                                        {firmaimg && (
                                                            <img 
                                                                src={firmaimg} 
                                                                alt="Firma profesional" 
                                                                style={{ maxWidth: '150px', maxHeight: '60px' }} 
                                                            />
                                                        )}
                                                        {!firmaimg && <small className="text-muted">Sin firma</small>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                            
                            {/* Mensaje cuando se está cargando la plantilla */}
                            {!plantillaSeleccionada && plantillaId && (
                                <div className="alert alert-info">
                                    <i className="fas fa-spinner fa-spin"></i> Cargando estructura de la plantilla...
                                </div>
                            )}
                            
                            {/* 🆕 Botones de Acción del Formulario - Movidos al final */}
                            {plantillaSeleccionada && (
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12 text-right">
                                                <button 
                                                    className="btn btn-secondary mr-2"
                                                    onClick={() => history.goBack()}
                                                    disabled={loading}
                                                >
                                                    <i className="fas fa-arrow-left"></i> Volver
                                                </button>
                                                <button 
                                                    className="btn btn-primary" 
                                                    type="submit" 
                                                    form="formHistoriaJson"
                                                    disabled={loading || !plantillaSeleccionada}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i> Guardando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-save"></i> Guardar Historia Clínica
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                            {/* 🆕 Columna del historial (3 columnas) */}
                            <div className="col-md-3" style={{ borderLeft: '2px solid #dee2e6' }}>
                                <div className="timeline-evolucion">
                                    <div className="titulo-historial" style={{ backgroundColor: '#f8f9fa', padding: '10px', border: '1px solid #dee2e6', marginBottom: '15px' }}>
                                        <i className="fas fa-history"></i> Historial de Historias Clínicas
                                    </div>
                                
                                    {historial.map((item) => (
                                        <div 
                                            className={`timeline-item-container ${historiaSeleccionada?.id === item.id ? 'selected' : ''}`}
                                            key={item.id} 
                                            onClick={() => verHistoria(item)} 
                                            style={{ 
                                                cursor: 'pointer',
                                                ...(historiaSeleccionada?.id === item.id ? {
                                                    color: '#17a2b8',
                                                    backgroundColor: '#d1ecf1',
                                                    border: '2px solid #17a2b8',
                                                    transition: 'all 0.3s'
                                                } : {})
                                            }}
                                        >
                                            <div className="timeline-punto-container">
                                                <div 
                                                    className="timeline-punto"
                                                    style={historiaSeleccionada?.id === item.id ? {
                                                        boxShadow: '0 0 0 4px rgba(23, 162, 184, 0.3)'
                                                    } : {}}
                                                ></div>
                                            </div>
                                            <div className="timeline-meta" style={{ fontSize: '10px', lineHeight: '1.4' }}>
                                                <span style={{ fontWeight: 'bold', fontSize: '11px' }}>
                                                    <i className="fas fa-calendar-alt"></i> {formatFecha(new Date(item.fecha))}
                                                </span>
                                                <br />
                                                <span style={{ color: '#6c757d', fontSize: '9px' }}>
                                                    {item.plantilla_nombre}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                
                                    {historial.length === 0 && (
                                        <div className="text-center text-muted" style={{ padding: '20px' }}>
                                            <i className="fas fa-inbox"></i>
                                            <br />
                                            Sin registros
                                        </div>
                                    )}
                                    
                                    {/* 🆕 Botón Resumen Completo */}
                                    <div className="text-center mt-3">
                                        <button 
                                            className="btn btn-primary btn-sm btn-resumen-historico"
                                            onClick={() => {
                                                if (historial && historial.length > 0) {
                                                    // Generar vista previa consolidada de todas las historias
                                                    generarVistaConsolidada();
                                                } else {
                                                    alert('No hay historias clínicas para mostrar');
                                                }
                                            }}
                                            disabled={historial.length === 0}
                                        >
                                            <i className="fas fa-file-alt"></i> Resumen Completo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
            
            {/* 🆕 Modal de Vista Previa */}
            <Modal show={showModalVistaPrevia} onHide={() => setShowModalVistaPrevia(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historia Clínica</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historiaSeleccionada && (
                        <div dangerouslySetInnerHTML={{ __html: generarContenidoHistoriaHTML(historiaSeleccionada) }} />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={descargarPdfHistoria}>
                        <i className="fas fa-file-pdf"></i> Guardar PDF
                    </Button>
                    <Button variant="primary" onClick={imprimirHistoria}>
                        <i className="fas fa-print"></i> Imprimir
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModalVistaPrevia(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* 🆕 Modal de Confirmación Post-Guardado */}
            <Modal show={showModalConfirmacion} onHide={handleCerrarModalConfirmacion} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-file-medical"></i> Historia Clínica Guardada
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                        La historia clínica ha sido guardada exitosamente. ¿Qué deseas hacer?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleImprimirDesdeConfirmacion}>
                        <i className="fas fa-print"></i> Imprimir
                    </Button>
                    <Button variant="danger" onClick={handleDescargarPdfDesdeConfirmacion}>
                        <i className="fas fa-file-pdf"></i> Descargar PDF
                    </Button>
                    <Button variant="secondary" onClick={handleCerrarModalConfirmacion}>
                        <i className="fas fa-times"></i> Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* 🆕 Modal de Vista Consolidada */}
            <Modal show={showModalConsolidado} onHide={() => setShowModalConsolidado(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historial Completo de Historias Clínicas</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div dangerouslySetInnerHTML={{ __html: consolidado }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalConsolidado(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default HistoriaClinicaJsonPage;
